import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CustomField } from '../../../models/custom-fields.model';
import { ObservationCustomFieldsService } from '../../../services/observation-custom-fields.service';
import { CustomFieldRendererService } from '../../../services/custom-field-renderer.service';

/**
 * Custom Fields Section Component
 *
 * A standardized component that renders the custom fields section
 * for observation creation forms. This ensures consistency across
 * all observation creation components.
 */
@Component({
  selector: 'app-custom-fields-section',
  templateUrl: './custom-fields-section.component.html',
  styleUrls: ['./custom-fields-section.component.scss'],
})
export class CustomFieldsSectionComponent implements OnInit {
  @Input() customFields$!: BehaviorSubject<CustomField[]>;
  @Input() isLoadingCustomFields$!: BehaviorSubject<boolean>;
  @Input() form!: FormGroup;
  @Input() disabled = false;
  displayFields$!: Observable<CustomField[]>;

  constructor(
    private observationCustomFields: ObservationCustomFieldsService,
    private fieldRenderer: CustomFieldRendererService,
  ) {}

  ngOnInit(): void {
    const formChanges$ = this.form ? this.form.valueChanges.pipe(startWith(null)) : of(null);

    // Merge fields provided from API with any dynamically registered/modified fields
    // Registered fields take precedence over base ones when names collide
    this.displayFields$ = combineLatest([this.customFields$, formChanges$]).pipe(
      map(([baseFields]) => {
        const base = baseFields || [];
        const registered = this.fieldRenderer.getRegisteredFields(this.form) || [];

        const byName = new Map<string, CustomField>();
        // Start with base fields
        for (const f of base) {
          byName.set(f.name, f);
        }
        // Overlay with registered (may include synthetic or dependency-adjusted versions)
        for (const f of registered) {
          byName.set(f.name, f);
        }

        return Array.from(byName.values());
      }),
    );
  }

  /**
   * Track by function for custom fields
   * Bound to component instance to avoid 'this' context issues
   */
  trackByField = (index: number, field: CustomField): string => {
    return this.observationCustomFields.trackByField(index, field);
  };
}
